import type React from 'react'
import type { DataNode } from 'antd/lib/tree'
import { DOMAIN_CONFIG } from './domainConfig'
import { generalToObjectiveType, objectiveToSpecificType } from './activityTypeMapping'
import { normalizeKey } from '../../shared/verteoffice/exports/helpers/helpers'

export interface ActivityGuideTreeNode extends DataNode {
  title: string
  level: 'general' | 'objective' | 'specific'
  children?: ActivityGuideTreeNode[]
}

const uniqueByNormalized = (values: string[]): string[] => {
  const seen = new Set<string>()
  const out: string[] = []

  values.forEach((value) => {
    const label = String(value ?? '').trim()
    if (!label) return

    const key = normalizeKey(label)
    if (seen.has(key)) return

    seen.add(key)
    out.push(label)
  })

  return out
}

const pathKey = (...parts: string[]) =>
  parts.map(part => normalizeKey(part).replace(/\s+/g, '_')).join('__')

function buildSpecificActivityNodes(
  generalName: string,
  objectiveName: string,
  domainDataList: Record<string, string[]>
) {
  const specificType = objectiveToSpecificType[normalizeKey(objectiveName)]
  const specConfig = specificType
    ? DOMAIN_CONFIG.find(c => normalizeKey(c.type) === normalizeKey(specificType))
    : undefined
  const specificActivities = specConfig
    ? uniqueByNormalized(domainDataList[specConfig.listKey] ?? [])
    : []

  return specificActivities.map<ActivityGuideTreeNode>(specificActivity => ({
    key: pathKey('specific', generalName, objectiveName, specificActivity),
    title: specificActivity,
    level: 'specific',
    isLeaf: true,
  }))
}

export function buildActivityGuideTree(domainDataList: Record<string, string[]>): ActivityGuideTreeNode[] {
  const generalActivities = uniqueByNormalized(domainDataList.generalActivityList ?? [])

  return generalActivities
    .map<ActivityGuideTreeNode | null>(generalActivityName => {
      const objectiveDomainType = generalToObjectiveType[normalizeKey(generalActivityName)]
      if (!objectiveDomainType) return null

      const objectiveConfig = DOMAIN_CONFIG.find(
        c => normalizeKey(c.type) === normalizeKey(objectiveDomainType)
      )
      if (!objectiveConfig) return null

      const objectives = uniqueByNormalized(domainDataList[objectiveConfig.listKey] ?? [])
      const objectiveNodes = objectives.map<ActivityGuideTreeNode>(objectiveName => ({
        key: pathKey('objective', generalActivityName, objectiveName),
        title: objectiveName,
        level: 'objective',
        children: buildSpecificActivityNodes(generalActivityName, objectiveName, domainDataList),
      }))

      return {
        key: pathKey('general', generalActivityName),
        title: generalActivityName,
        level: 'general',
        children: objectiveNodes,
      }
    })
    .filter((node): node is ActivityGuideTreeNode => Boolean(node))
}

export function filterActivityGuideTree(
  treeData: ActivityGuideTreeNode[],
  rawSearchTerm: string
): { filteredTree: ActivityGuideTreeNode[]; expandedKeys: React.Key[] } {
  const searchTerm = normalizeKey(rawSearchTerm)

  if (!searchTerm) {
    return { filteredTree: treeData, expandedKeys: [] }
  }

  const expandedKeys = new Set<React.Key>()

  const filterNode = (node: ActivityGuideTreeNode): ActivityGuideTreeNode | null => {
    const matches = normalizeKey(node.title).includes(searchTerm)
    const filteredChildren = (node.children ?? [])
      .map(filterNode)
      .filter((child): child is ActivityGuideTreeNode => Boolean(child))

    if (!matches && filteredChildren.length === 0) {
      return null
    }

    if (filteredChildren.length > 0 && node.children?.length) {
      expandedKeys.add(node.key)
    }

    return {
      ...node,
      children: filteredChildren.length > 0 ? filteredChildren : node.children ? [] : undefined,
    }
  }

  return {
    filteredTree: treeData
      .map(filterNode)
      .filter((node): node is ActivityGuideTreeNode => Boolean(node)),
    expandedKeys: Array.from(expandedKeys),
  }
}
